import React, { useState, useEffect } from 'react';
import { PromoCode, PromoCodeInput } from '@/hooks/sms/types';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface Restaurant {
  id: string;
  name: string;
}

interface PromoCodeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: PromoCodeInput) => Promise<void>;
  onSaveMultiple?: (data: PromoCodeInput, restaurantIds: string[]) => Promise<void>;
  codeToEdit?: PromoCode | null;
  restaurantId?: string;
  organisationId?: string;
  restaurants: Restaurant[];
  isLoading?: boolean;
  isDuplicating?: boolean;
}

const PromoCodeDialog: React.FC<PromoCodeDialogProps> = ({
  isOpen,
  onOpenChange,
  onSave,
  onSaveMultiple,
  codeToEdit,
  restaurantId,
  organisationId,
  restaurants,
  isLoading = false,
  isDuplicating = false
}) => {
  const [formData, setFormData] = useState<PromoCodeInput>({
    code: '',
    name: '',
    restaurant_id: restaurantId || '',
    organisation_id: organisationId,
    discount_type: 'percentage_off',
    discount_value: null,
    item_cost: null,
    item_menu_price: null,
    min_order_value: undefined,
    max_discount: undefined,
    start_date: undefined,
    end_date: undefined,
    usage_limit: undefined,
    active: true
  });

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([]);
  const [useMultipleRestaurants, setUseMultipleRestaurants] = useState(false);

  // Reset form when dialog opens/closes or when editing different code
  useEffect(() => {
    if (isOpen) {
      if (codeToEdit) {
        setFormData({
          code: isDuplicating ? `Copy of ${codeToEdit.code}` : codeToEdit.code,
          name: isDuplicating ? `Copy of ${codeToEdit.name}` : codeToEdit.name,
          restaurant_id: codeToEdit.restaurant_id,
          organisation_id: codeToEdit.organisation_id,
          discount_type: codeToEdit.discount_type,
          discount_value: codeToEdit.discount_value,
          item_cost: codeToEdit.item_cost || null,
          item_menu_price: codeToEdit.item_menu_price || null,
          min_order_value: codeToEdit.min_order_value,
          max_discount: codeToEdit.max_discount,
          start_date: codeToEdit.start_date,
          end_date: codeToEdit.end_date,
          usage_limit: codeToEdit.usage_limit,
          active: codeToEdit.active
        });
        setStartDate(codeToEdit.start_date ? new Date(codeToEdit.start_date) : undefined);
        setEndDate(codeToEdit.end_date ? new Date(codeToEdit.end_date) : undefined);
        setSelectedRestaurants([codeToEdit.restaurant_id]);
        setUseMultipleRestaurants(false);
      } else {
        // Reset to initial values
        setFormData({
          code: '',
          name: '',
          restaurant_id: restaurantId || '',
          organisation_id: organisationId,
          discount_type: 'percentage_off',
          discount_value: null,
          item_cost: null,
          item_menu_price: null,
          min_order_value: null,
          max_discount: null,
          start_date: undefined,
          end_date: undefined,
          usage_limit: null,
          active: true
        });
        setStartDate(undefined);
        setEndDate(undefined);
        setSelectedRestaurants(restaurantId ? [restaurantId] : []);
        setUseMultipleRestaurants(false);
      }
      setErrors({});
    }
  }, [isOpen, codeToEdit, isDuplicating, restaurantId, organisationId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (formData.code.trim().length < 2) {
      newErrors.code = 'Code must be at least 2 characters';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (useMultipleRestaurants) {
      if (selectedRestaurants.length === 0) {
        newErrors.restaurant_id = 'At least one restaurant is required';
      }
    } else {
      if (!formData.restaurant_id) {
        newErrors.restaurant_id = 'Restaurant is required';
      }
    }

    // Remove discount value validation for free_item type
    if (formData.discount_type !== 'free_item' && (!formData.discount_value || formData.discount_value <= 0)) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }

    if (formData.discount_type === 'percentage_off' && formData.discount_value && formData.discount_value > 100) {
      newErrors.discount_value = 'Percentage cannot exceed 100%';
    }

    if (formData.discount_type === 'free_item') {
      if (!formData.item_cost || formData.item_cost <= 0) {
        newErrors.item_cost = 'Item cost is required for free item discounts';
      }
      if (!formData.item_menu_price || formData.item_menu_price <= 0) {
        newErrors.item_menu_price = 'Menu price is required for free item discounts';
      }
    }

    if (formData.min_order_value && formData.min_order_value < 0) {
      newErrors.min_order_value = 'Minimum order value cannot be negative';
    }

    if (formData.usage_limit && formData.usage_limit < 1) {
      newErrors.usage_limit = 'Usage limit must be at least 1';
    }

    if (startDate && endDate && startDate > endDate) {
      newErrors.end_date = 'End date must be on or after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload: PromoCodeInput = {
      ...formData,
      code: formData.code.toUpperCase().trim(),
      // For free_item type, discount_value should be null, otherwise convert null to 0
      discount_value: formData.discount_type === 'free_item' ? null : (formData.discount_value || 0),
      item_cost: formData.item_cost || 0,
      item_menu_price: formData.item_menu_price || 0,
      start_date: startDate ? startDate.toISOString().split('T')[0] : undefined,
      end_date: endDate ? endDate.toISOString().split('T')[0] : undefined,
    };

    if (useMultipleRestaurants && onSaveMultiple) {
      await onSaveMultiple(payload, selectedRestaurants);
    } else {
      await onSave(payload);
    }
  };

  const isEditing = !isDuplicating && !!codeToEdit;
  const title = isDuplicating ? 'Duplicate Promo Code' : isEditing ? 'Edit Promo Code' : 'Create Promo Code';
  const description = isDuplicating ? 'Create a copy of this promo code' : isEditing ? 'Update the promo code details' : 'Create a new promo code for your campaigns';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="code">Promo Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="SAVE20"
                className={errors.code ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">Code for customers to enter when checking out. Consider ease of sharing when selecting code</p>
              {errors.code && <p className="text-xs text-destructive">{errors.code}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">Display Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="2-for-1 Burgers"
                className={errors.name ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">Name to be used in message templates (e.g., "FREE Dessert" or "2-for-1 Burgers")</p>
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>
          </div>

          {/* Restaurant Selection */}
          <div className="grid gap-2">
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="multiple-restaurants"
                checked={useMultipleRestaurants}
                onCheckedChange={(checked) => {
                  setUseMultipleRestaurants(!!checked);
                  if (!checked) {
                    setSelectedRestaurants([]);
                    setFormData({ ...formData, restaurant_id: '' });
                  }
                }}
                disabled={isEditing} // Disable for editing existing codes
              />
              <Label htmlFor="multiple-restaurants">Create for multiple restaurants</Label>
            </div>

            {useMultipleRestaurants ? (
              <div>
                <Label>Select Restaurants *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {restaurants.map((restaurant) => (
                    <div key={restaurant.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`restaurant-${restaurant.id}`}
                        checked={selectedRestaurants.includes(restaurant.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRestaurants([...selectedRestaurants, restaurant.id]);
                          } else {
                            setSelectedRestaurants(selectedRestaurants.filter(id => id !== restaurant.id));
                          }
                        }}
                      />
                      <Label htmlFor={`restaurant-${restaurant.id}`} className="text-sm">
                        {restaurant.name}
                      </Label>
                    </div>
                  ))}
                </div>
                {errors.restaurant_id && <p className="text-xs text-destructive">{errors.restaurant_id}</p>}
              </div>
            ) : (
              <div>
                <Label htmlFor="restaurant">Restaurant *</Label>
                <Select 
                  value={formData.restaurant_id} 
                  onValueChange={(value) => setFormData({ ...formData, restaurant_id: value })}
                >
                  <SelectTrigger className={errors.restaurant_id ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select restaurant" />
                  </SelectTrigger>
                  <SelectContent>
                    {restaurants.map((restaurant) => (
                      <SelectItem key={restaurant.id} value={restaurant.id}>
                        {restaurant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.restaurant_id && <p className="text-xs text-destructive">{errors.restaurant_id}</p>}
              </div>
            )}
          </div>

          {/* Discount Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="discount-type">Discount Type *</Label>
              <Select 
                value={formData.discount_type} 
                onValueChange={(value: 'percentage_off' | 'dollar_off' | 'free_item') => 
                  setFormData({ ...formData, discount_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage_off">Percentage Off</SelectItem>
                  <SelectItem value="dollar_off">Dollar Amount Off</SelectItem>
                  <SelectItem value="free_item">Free Item</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.discount_type !== 'free_item' && (
              <div className="grid gap-2">
                <Label htmlFor="discount-value">
                  {formData.discount_type === 'percentage_off' ? 'Percentage (%)' : 'Dollar Amount ($)'} *
                </Label>
                <Input
                  id="discount-value"
                  type="number"
                  value={formData.discount_value || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    discount_value: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder={formData.discount_type === 'percentage_off' ? '20' : '5.00'}
                  min="0"
                  max={formData.discount_type === 'percentage_off' ? '100' : undefined}
                  step={formData.discount_type === 'percentage_off' ? '1' : '0.01'}
                  className={errors.discount_value ? 'border-destructive' : ''}
                />
                {errors.discount_value && <p className="text-xs text-destructive">{errors.discount_value}</p>}
              </div>
            )}
          </div>

          {/* Free Item Fields */}
          {formData.discount_type === 'free_item' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="item-cost">Item Cost (COGS) *</Label>
                <Input
                  id="item-cost"
                  type="number"
                  value={formData.item_cost || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    item_cost: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="5.00"
                  min="0"
                  step="0.01"
                  className={errors.item_cost ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">Cost of goods sold for the free item</p>
                {errors.item_cost && <p className="text-xs text-destructive">{errors.item_cost}</p>}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="item-menu-price">Menu Price *</Label>
                <Input
                  id="item-menu-price"
                  type="number"
                  value={formData.item_menu_price || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    item_menu_price: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="15.00"
                  min="0"
                  step="0.01"
                  className={errors.item_menu_price ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">Regular menu price of the free item</p>
                {errors.item_menu_price && <p className="text-xs text-destructive">{errors.item_menu_price}</p>}
              </div>
            </div>
          )}

          {/* Optional Constraints - Row 1 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="min-order">Minimum Order Value</Label>
              <Input
                id="min-order"
                type="number"
                value={formData.min_order_value || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  min_order_value: e.target.value ? parseFloat(e.target.value) : null 
                })}
                placeholder="25.00"
                min="0"
                step="0.01"
                className={errors.min_order_value ? 'border-destructive' : ''}
              />
              {errors.min_order_value && <p className="text-xs text-destructive">{errors.min_order_value}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="usage-limit">Usage Limit</Label>
              <Input
                id="usage-limit"
                type="number"
                value={formData.usage_limit || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  usage_limit: e.target.value ? parseInt(e.target.value) : null 
                })}
                placeholder="100"
                min="1"
                className={errors.usage_limit ? 'border-destructive' : ''}
              />
              {errors.usage_limit && <p className="text-xs text-destructive">{errors.usage_limit}</p>}
            </div>
          </div>

          {/* Optional Constraints - Row 2 (Only for Percentage Off) */}
          {formData.discount_type === 'percentage_off' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="max-discount">Maximum Discount ($)</Label>
                <Input
                  id="max-discount"
                  type="number"
                  value={formData.max_discount || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_discount: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="50.00"
                  min="0"
                  step="0.01"
                />
                <p className="text-xs text-muted-foreground">Maximum dollar amount for percentage discounts</p>
              </div>
              <div></div> {/* Empty column for consistent layout */}
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid gap-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "justify-start text-left font-normal",
                      !endDate && "text-muted-foreground",
                      errors.end_date && "border-destructive"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.end_date && <p className="text-xs text-destructive">{errors.end_date}</p>}
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
            />
            <Label htmlFor="active">Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : isDuplicating ? 'Create Copy' : isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromoCodeDialog;